<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useAlert } from "dashboard/composables";
import LeadCaptureAPI from "dashboard/api/leadCaptureEndpoints";
import LeadCaptureForm from "./LeadCaptureForm.vue";
import LeadCaptureSnippet from "./LeadCaptureSnippet.vue";

const store = useStore();
const endpoints = ref([]);
const loading = ref(false);
const showForm = ref(false);
const editing = ref(null);
const snippetFor = ref(null);

// Phase C-4: aba de leads recebidos
const tab = ref("endpoints");
const leads = ref([]);
const loadingLeads = ref(false);

const inboxes = computed(() => store.getters["inboxes/getInboxes"] || []);

async function loadLeads() {
  loadingLeads.value = true;
  try {
    const { data } = await LeadCaptureAPI.getLeads();
    leads.value = Array.isArray(data?.leads) ? data.leads : [];
  } catch (e) {
    useAlert("Erro ao carregar leads");
  } finally {
    loadingLeads.value = false;
  }
}

function switchTab(name) {
  tab.value = name;
  if (name === "leads" && leads.value.length === 0) loadLeads();
}

// Termometro: qualificacao (Phase A) tem prioridade; senao o perfil do lead score.
function qualifBadge(lead) {
  if (lead.qualified === true) return { label: "Qualificado", cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" };
  if (lead.qualified === false) return { label: "Desqualificado", cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" };
  const p = (lead.profile || "").toLowerCase();
  if (p === "hot") return { label: "Quente", cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" };
  if (p === "warm") return { label: "Morno", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" };
  if (p === "cold") return { label: "Frio", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" };
  return { label: "—", cls: "bg-n-slate-3 text-n-slate-11" };
}

function answersPreview(answers) {
  return Object.entries(answers || {})
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
    .join(" · ");
}

function humanDate(d) {
  return d ? new Date(d).toLocaleString("pt-BR") : "-";
}

async function load() {
  loading.value = true;
  try {
    const { data } = await LeadCaptureAPI.get();
    endpoints.value = Array.isArray(data) ? data : [];
  } catch (e) {
    useAlert("Erro ao carregar capturadores");
  } finally {
    loading.value = false;
  }
}

function startCreate() { editing.value = null; showForm.value = true; }
function startEdit(ep) { editing.value = ep; showForm.value = true; }

async function destroy(ep) {
  if (!window.confirm("Excluir o capturador? Os links associados deixarao de funcionar.")) return;
  try { await LeadCaptureAPI.delete(ep.id); useAlert("Capturador removido"); await load(); }
  catch (e) { useAlert("Erro ao remover"); }
}

async function rotate(ep) {
  if (!window.confirm("Gerar um novo slug? O link atual deixa de funcionar imediatamente.")) return;
  try { await LeadCaptureAPI.rotateSlug(ep.id); useAlert("Slug rotacionado"); await load(); }
  catch (e) { useAlert("Erro ao rotacionar slug"); }
}

async function toggleActive(ep) {
  try { await LeadCaptureAPI.update(ep.id, { active: !ep.active }); await load(); }
  catch (e) { useAlert("Erro ao alternar status"); }
}

function copyText(text) {
  navigator.clipboard.writeText(text).then(() => useAlert("Copiado!"));
}

function onFormSaved() { showForm.value = false; editing.value = null; load(); }

const labelByType = { wa_redirect: "Botao WhatsApp", webhook: "Webhook (form)" };

onMounted(() => { store.dispatch("inboxes/get"); load(); });
</script>

<template>
  <div class="p-6 max-w-screen-xl mx-auto">
    <header class="flex justify-between items-start gap-6 mb-6">
      <div>
        <h2 class="text-lg font-extrabold text-n-slate-12 mb-1">Gerador de Link de Captura</h2>
        <p class="text-sm text-n-slate-11 max-w-2xl">
          Cria links e webhooks que capturam <code class="px-1 py-0.5 rounded bg-n-slate-3 text-xs">gclid</code>,
          <code class="px-1 py-0.5 rounded bg-n-slate-3 text-xs">fbclid</code>, UTMs etc.
          e registram tudo no painel ja vinculado ao lead. Use pra anuncios Google Ads, Meta CTWA
          ou forms externos (Tally, Typeform, n8n, Hubspot).
        </p>
      </div>
      <button
        v-if="tab === 'endpoints'"
        class="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-n-brand text-white hover:bg-n-brand/90 transition shrink-0"
        @click="startCreate"
      >+ Novo capturador</button>
    </header>

    <!-- Phase C-4: abas Capturadores | Leads recebidos -->
    <div class="flex gap-1 mb-5 border-b border-n-slate-4">
      <button
        v-for="t in [{ k: 'endpoints', l: 'Capturadores' }, { k: 'leads', l: 'Leads recebidos' }]"
        :key="t.k"
        class="px-4 py-2 text-sm font-medium -mb-px border-b-2 transition"
        :class="tab === t.k ? 'border-n-brand text-n-slate-12' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
        @click="switchTab(t.k)"
      >{{ t.l }}</button>
    </div>

    <template v-if="tab === 'endpoints'">
    <div v-if="loading" class="text-n-slate-11 text-sm">Carregando...</div>

    <div v-else-if="endpoints.length === 0" class="p-12 text-center bg-n-slate-2 rounded-xl text-n-slate-11">
      Voce ainda nao criou nenhum capturador. Clique em <b>+ Novo capturador</b> pra comecar.
    </div>

    <div v-else class="overflow-x-auto rounded-xl border border-n-slate-4">
      <table class="w-full text-sm">
        <thead class="bg-n-slate-2 text-n-slate-11">
          <tr>
            <th class="text-left px-4 py-3 font-semibold">Nome</th>
            <th class="text-left px-4 py-3 font-semibold">Tipo</th>
            <th class="text-left px-4 py-3 font-semibold">Inbox</th>
            <th class="text-left px-4 py-3 font-semibold">URL</th>
            <th class="text-left px-4 py-3 font-semibold">Hits</th>
            <th class="text-left px-4 py-3 font-semibold">Ultimo</th>
            <th></th>
          </tr>
        </thead>
        <tbody class="text-n-slate-12">
          <tr v-for="ep in endpoints" :key="ep.id" :class="['border-t border-n-slate-4', !ep.active && 'opacity-50']">
            <td class="px-4 py-3 align-top">
              <span class="font-semibold">{{ ep.name }}</span>
              <span v-if="!ep.active" class="ml-2 px-2 py-0.5 rounded text-xs bg-n-slate-3">desativado</span>
            </td>
            <td class="px-4 py-3 align-top">{{ labelByType[ep.endpoint_type] || ep.endpoint_type }}</td>
            <td class="px-4 py-3 align-top">{{ ep.inbox_name }}</td>
            <td class="px-4 py-3 align-top min-w-[280px] max-w-[420px]">
              <div class="flex items-center gap-2">
                <code class="flex-1 text-xs truncate bg-n-slate-2 px-2 py-1.5 rounded font-mono" :title="ep.public_url">{{ ep.public_url }}</code>
                <button
                  class="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded border border-n-slate-4 hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12"
                  title="Copiar URL"
                  @click="copyText(ep.public_url)"
                ><span class="i-ri-file-copy-line w-5 h-5" /></button>
              </div>
            </td>
            <td class="px-4 py-3 align-top">{{ ep.hits_count }}</td>
            <td class="px-4 py-3 align-top whitespace-nowrap">{{ ep.last_hit_at ? new Date(ep.last_hit_at).toLocaleString("pt-BR") : "-" }}</td>
            <td class="px-4 py-3 align-top">
              <div class="flex items-center gap-1">
                <button
                  class="inline-flex items-center justify-center w-10 h-10 rounded border border-n-slate-4 hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12"
                  title="Ver snippet de codigo"
                  @click="snippetFor = ep"
                ><span class="i-ri-code-box-line w-5 h-5" /></button>
                <button
                  class="inline-flex items-center justify-center w-10 h-10 rounded border border-n-slate-4 hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12"
                  title="Editar"
                  @click="startEdit(ep)"
                ><span class="i-ri-pencil-line w-5 h-5" /></button>
                <button
                  class="inline-flex items-center justify-center w-10 h-10 rounded border border-n-slate-4 hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12"
                  :title="ep.active ? 'Desativar' : 'Ativar'"
                  @click="toggleActive(ep)"
                ><span :class="[ep.active ? 'i-ri-toggle-line' : 'i-ri-toggle-line', 'w-5 h-5']" /></button>
                <button
                  class="inline-flex items-center justify-center w-10 h-10 rounded border border-n-slate-4 hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12"
                  title="Rotacionar slug (gera nova URL e invalida a atual)"
                  @click="rotate(ep)"
                ><span class="i-ri-refresh-line w-5 h-5" /></button>
                <button
                  class="inline-flex items-center justify-center w-10 h-10 rounded border border-ruby-4 text-ruby-9 hover:bg-ruby-3"
                  title="Excluir"
                  @click="destroy(ep)"
                ><span class="i-ri-delete-bin-line w-5 h-5" /></button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    </template>

    <!-- Phase C-4: tabela de leads recebidos + termometro de qualificacao -->
    <div v-else-if="tab === 'leads'">
      <div v-if="loadingLeads" class="text-n-slate-11 text-sm">Carregando leads...</div>
      <div v-else-if="leads.length === 0" class="p-12 text-center bg-n-slate-2 rounded-xl text-n-slate-11">
        Nenhum lead de formulario ainda. Quando entrarem leads (webhook ou Meta Lead Ads), eles aparecem aqui com as respostas e o termometro de qualificacao.
      </div>
      <div v-else class="overflow-x-auto rounded-xl border border-n-slate-4">
        <table class="w-full text-sm">
          <thead class="bg-n-slate-2 text-n-slate-11">
            <tr>
              <th class="text-left px-4 py-3 font-semibold">Lead</th>
              <th class="text-left px-4 py-3 font-semibold">Termometro</th>
              <th class="text-left px-4 py-3 font-semibold">Respostas do formulario</th>
              <th class="text-left px-4 py-3 font-semibold">Recebido</th>
            </tr>
          </thead>
          <tbody class="text-n-slate-12">
            <tr v-for="lead in leads" :key="lead.id" class="border-t border-n-slate-4 align-top">
              <td class="px-4 py-3">
                <a :href="`/app/accounts/${$route.params.accountId}/contacts/${lead.id}`" class="font-semibold text-n-brand hover:underline">{{ lead.name || "Lead" }}</a>
                <div class="text-xs text-n-slate-11 mt-0.5">{{ lead.phone }}</div>
                <div v-if="lead.email" class="text-xs text-n-slate-11">{{ lead.email }}</div>
              </td>
              <td class="px-4 py-3">
                <span class="inline-flex px-2 py-0.5 rounded text-xs font-medium" :class="qualifBadge(lead).cls">{{ qualifBadge(lead).label }}</span>
                <div v-if="lead.score != null" class="text-xs text-n-slate-11 mt-1">score {{ lead.score }}</div>
              </td>
              <td class="px-4 py-3 max-w-[460px]">
                <span class="text-xs text-n-slate-11 break-words">{{ answersPreview(lead.answers) || "—" }}</span>
                <div v-if="lead.labels && lead.labels.length" class="flex flex-wrap gap-1 mt-1">
                  <span v-for="l in lead.labels" :key="l" class="px-1.5 py-0.5 rounded text-[10px] bg-n-slate-3 text-n-slate-11">{{ l }}</span>
                </div>
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-xs text-n-slate-11">{{ humanDate(lead.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <LeadCaptureForm
      v-if="showForm"
      :endpoint="editing"
      :inboxes="inboxes"
      @saved="onFormSaved"
      @close="showForm = false"
    />
    <LeadCaptureSnippet
      v-if="snippetFor"
      :endpoint="snippetFor"
      @close="snippetFor = null"
    />
  </div>
</template>
